import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LayoutDashboard, Star, LogOut, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import sounds from "@/utils/sounds";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";
  const { t } = useLanguage();

  // Resume audio context on first interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      sounds.resume();
      window.removeEventListener('click', handleFirstInteraction);
    };
    window.addEventListener('click', handleFirstInteraction);
    return () => window.removeEventListener('click', handleFirstInteraction);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location]);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  }, [location]);

  const handleLogout = () => {
    sounds.click();
    try { localStorage.removeItem("token"); } catch {}
    try { localStorage.removeItem("user"); } catch {}
    setUser(null);
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const navLinks = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.services"), path: "/services" },
    { name: t("gallery.title"), path: "/gallery" },
    { name: t("nav.reviews"), path: "/reviews" },
    { name: t("nav.contact"), path: "/contact" },
  ];

  const headerBg = isScrolled || !isHomePage
    ? "bg-white/95 backdrop-blur-md shadow-sm"
    : "bg-transparent";

  const textColor = isScrolled || !isHomePage
    ? "text-navy-900"
    : "text-white";

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-300 ${headerBg} ${
        isScrolled ? "py-3" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className={`flex items-center gap-2 ${textColor} transition-colors`}
          >
            <img 
              src="/logo.png" 
              alt="Shylov Cleaning Logo" 
              className="h-10 md:h-12 w-auto"
            />
            <div className="hidden md:block">
              <span className="font-accent text-lg font-semibold tracking-wide block leading-tight">
                Shylov Cleaning
              </span>
              <span className="text-xs tracking-wider opacity-70">Yachting d'Exception</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={link.path}
                  onClick={() => sounds.click()}
                  onMouseEnter={() => sounds.hover()}
                  data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                  className={`${textColor} text-sm font-medium hover:text-marine-600 transition-colors duration-300 relative group`}
                >
                  <motion.span
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                    className="inline-block"
                  >
                    {link.name}
                  </motion.span>
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-marine-500 to-marine-600 transition-all duration-300 ${
                      location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              </motion.div>
            ))}
            
            <LanguageSwitcher variant={isScrolled || !isHomePage ? "dark" : "light"} />
            
            {user ? (
              /* Logged in user menu */
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  data-testid="user-menu-btn"
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${textColor} hover:text-marine-600`}
                >
                  <div className="w-8 h-8 rounded-full bg-marine-600 flex items-center justify-center text-white font-semibold">
                    {user.first_name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <span className="hidden xl:inline">{user.first_name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isUserMenuOpen ? "rotate-180" : ""}`} />
                </button>
                
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-56 bg-white shadow-xl rounded-lg overflow-hidden border border-slate-100"
                    >
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                        <p className="font-medium text-navy-900">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email || user.phone}</p>
                      </div>
                      <div className="py-2">
                        <Link
                          to="/dashboard"
                          data-testid="menu-dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy-900 hover:bg-slate-50 transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4 text-marine-600" />
                          Dashboard
                        </Link>
                        <Link
                          to="/profile"
                          data-testid="menu-profile"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy-900 hover:bg-slate-50 transition-colors"
                        >
                          <User className="w-4 h-4 text-marine-600" />
                          {t("nav.profile") || "Profile"}
                        </Link>
                        <Link
                          to="/reviews/new"
                          data-testid="menu-review"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy-900 hover:bg-slate-50 transition-colors"
                        >
                          <Star className="w-4 h-4 text-marine-600" />
                          Leave Review
                        </Link>
                      </div>
                      <div className="border-t border-slate-100 py-2">
                        <button
                          onClick={handleLogout}
                          data-testid="menu-logout"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors w-full"
                        >
                          <LogOut className="w-4 h-4" />
                          {t("nav.logout") || "Logout"}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              /* Not logged in */
              <Link
                to="/login"
                data-testid="login-btn"
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${textColor} hover:text-marine-600`}
              >
                <User className="w-4 h-4" />
                {t("nav.login")}
              </Link>
            )}
            
            <Link
              to="/contact"
              data-testid="order-service-btn"
              className={`px-6 py-3 text-sm uppercase tracking-wider font-semibold transition-all duration-300 ${
                isScrolled || !isHomePage
                  ? "bg-navy-900 text-white hover:bg-navy-800"
                  : "bg-white text-navy-900 hover:bg-white/90"
              }`}
            >
              {t("nav.orderService")}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 lg:hidden">
            <LanguageSwitcher variant={isScrolled || !isHomePage ? "dark" : "light"} />
            <button
              data-testid="mobile-menu-btn"
              className={`${textColor}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
                  className={`block text-navy-900 text-base uppercase tracking-wider font-medium py-2 ${
                    location.pathname === link.path ? "text-marine-600" : ""
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {user ? (
                /* Mobile user menu */
                <div className="pt-4 border-t border-slate-100 space-y-2">
                  <div className="flex items-center gap-3 py-2">
                    <div className="w-10 h-10 rounded-full bg-marine-600 flex items-center justify-center text-white font-semibold">
                      {user.first_name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div>
                      <p className="font-medium text-navy-900">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-slate-500">{user.email || user.phone}</p>
                    </div>
                  </div>
                  <Link
                    to="/dashboard"
                    data-testid="mobile-dashboard"
                    className="flex items-center gap-3 py-2 text-navy-900"
                  >
                    <LayoutDashboard className="w-5 h-5 text-marine-600" />
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    data-testid="mobile-profile"
                    className="flex items-center gap-3 py-2 text-navy-900"
                  >
                    <User className="w-5 h-5 text-marine-600" />
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-logout"
                    className="flex items-center gap-3 py-2 text-red-600 w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </div>
              ) : (
                /* Mobile not logged in */
                <div className="flex gap-4 pt-4">
                  <Link
                    to="/login"
                    data-testid="mobile-login-btn"
                    className="flex-1 border border-navy-900 text-navy-900 text-center px-4 py-3 text-sm uppercase tracking-wider font-semibold"
                  >
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/register"
                    data-testid="mobile-register-btn"
                    className="flex-1 bg-navy-900 text-white text-center px-4 py-3 text-sm uppercase tracking-wider font-semibold"
                  >
                    {t("nav.register")}
                  </Link>
                </div>
              )}
              
              <Link
                to="/contact"
                data-testid="mobile-order-service-btn"
                className="block bg-marine-600 text-white text-center px-6 py-4 text-sm uppercase tracking-wider font-semibold"
              >
                {t("nav.orderService")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
